import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router";

import { leaveForLogin } from "@/api/client";
import logo from "@/assets/logo.gif";
import { signOut } from "@/auth/api";
import { can, useMe } from "@/auth/useMe";
import { ConnectionBadge } from "@/components/ConnectionBadge";
import { useUnreadConversations } from "@/inbox/queries";
import { useUnreadMail } from "@/mail/queries";
import { CallBar } from "@/telephony/CallBar";

/**
 * The seven destinations the design's nav declares (.docs/08 §1).
 *
 * One of them has no backend and no scope — Mailing. It ships visibly
 * disabled with its name intact rather than as a link that goes nowhere: a
 * seller who clicks a dead link files a bug, and a seller who sees a greyed
 * item understands the app. Agenda joined the live four in F4, Correo with P8
 * (.docs/09) and Tarjetas with the card index (`crm26.cards`).
 *
 * Actividades is the one destination the design does not draw: completed
 * work, the agenda's other half. It sits beside the agenda because that is
 * where a seller looks for what they did as well as what they have to do.
 *
 * Llamar is the other: a keypad for a number nobody has on file. It opens the
 * three channels — call, mail, WhatsApp — because it is the one that needs no
 * record to start from.
 */
const NAV = [
  { to: "/", label: "nav.dashboard", end: true },
  { to: "/prospectos", label: "nav.prospects", end: false },
  { to: "/agenda", label: "nav.agenda", end: false },
  { to: "/actividades", label: "nav.activities", end: false },
  { to: "/llamar", label: "nav.call", end: false },
  { to: "/correo", label: "nav.mail", end: false },
  { to: "/conversaciones", label: "nav.inbox", end: false },
  { to: "/tarjetas", label: "nav.cards", end: false },
  { to: null, label: "nav.mailing" },
] as const;

/** Initials for the avatar. Two words at most — "MP", never "MPDLC". */
function initials(name: string | undefined, email: string | undefined): string {
  const source = (name ?? "").trim() || (email ?? "").split("@")[0] || "";
  const words = source
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2);
  return words.map((word) => word[0]?.toUpperCase() ?? "").join("") || "·";
}

/** The two destinations that carry a count. */
const INBOX = "/conversaciones";
const MAIL = "/correo";

/**
 * The red count beside *WhatsApp* and *Correo*. Absent at zero: a badge that is
 * always there is a badge nobody sees.
 */
function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden="true"
      className="ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1 align-middle text-[11px] leading-none font-bold text-ground"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

/**
 * The frame every screen renders inside.
 *
 * An app shell, not a document: `h-dvh` with the scroll inside `<main>`, so
 * the header and the call bar stay put and a screen can ask for the height it
 * has left with a plain `h-full`. The inbox needs that — a three-pane layout
 * cannot be built against a viewport whose header height changes when the nav
 * wraps (.docs/04 §7).
 *
 * `dvh` rather than `vh` on purpose: on iOS Safari `100vh` is the height with
 * the URL bar *hidden*, so a `vh`-sized shell puts its bottom row under the
 * browser chrome — which here would be the hang-up button.
 */
export function Shell() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [leaving, setLeaving] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const unread = useUnreadConversations();
  const unreadMail = useUnreadMail();
  const waiting = unread.data?.conversations ?? 0;
  const mail = unreadMail.data?.messages ?? 0;
  // Ridden along with the badge: no second request per screen just to
  // learn that a mailbox stopped syncing (.docs/09 §8.1).
  const mailStopped = unreadMail.data?.needs_attention ?? false;
  const unreadId = useId();
  const mailId = useId();

  // Described only while something waits. Each link keeps its name — "WhatsApp",
  // "Correo" — so nothing that finds it by name breaks, and a screen reader
  // hears the count after it rather than a "0" on every visit.
  const badges: Record<string, { count: number; id: string }> = {
    [INBOX]: { count: waiting, id: unreadId },
    [MAIL]: { count: mail, id: mailId },
  };
  const badgeCount = (to: string) => badges[to]?.count ?? 0;
  const describedBy = (to: string | null) => {
    const badge = to === null ? undefined : badges[to];
    return badge && badge.count > 0 ? { "aria-describedby": badge.id } : {};
  };
  // The avatar stands in for both counts on a phone, so it is described by
  // whichever of them has something waiting.
  const menuDescribedBy = [waiting > 0 ? unreadId : null, mail > 0 ? mailId : null]
    .filter(Boolean)
    .join(" ");

  // One panel serves two jobs: the account links at every width, and the whole
  // nav below 900px where eight inline links do not fit. Two separate menus
  // would be two things to keep in step for no gain.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    function onPointerDown(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [menuOpen]);

  async function leave() {
    setLeaving(true);
    setSignOutFailed(false);
    if (await signOut()) {
      // A page load, not a router push: it is what drops the query cache, the
      // socket and the softphone of the session that just ended. `leaving`
      // stays set so the button does not wake up while the browser is going.
      leaveForLogin();
      return;
    }
    // Still signed in, as far as anyone can tell. Saying "you are out" to
    // someone walking away from a shared desk would be the wrong mistake.
    setLeaving(false);
    setSignOutFailed(true);
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? "font-display font-extrabold border-b-2 border-brand pb-0.5 text-ink"
      : "text-ink";

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-ground text-ink">
      <header className="nav relative shrink-0 gap-4 px-4 sm:px-8">
        {waiting > 0 ? (
          <span id={unreadId} className="sr-only">
            {t("nav.unread", { count: waiting })}
          </span>
        ) : null}
        {mail > 0 ? (
          <span id={mailId} className="sr-only">
            {t("nav.unreadMail", { count: mail })}
          </span>
        ) : null}
        <img
          src={logo}
          alt={t("app.name")}
          className="mr-auto h-7 w-auto sm:h-[38px]"
        />

        {/* Inline from 900px — the design's tablet width (D20) — up. Below it
            the same links live in the panel. Not `sm` or `md`: nine links need
            some 780px, and short of that they push the account button off the
            screen, and with it the only way into the panel. The gap is the
            tight one until `lg`: at 900px it is what leaves room for the two
            unread counts beside the ninth link. */}
        <nav className="hidden items-center gap-3 min-[900px]:flex lg:gap-6">
          {NAV.map((item) =>
            item.to === null ? (
              <span
                key={item.label}
                className="cursor-not-allowed text-sm text-mute-500"
                title={t("nav.soon")}
              >
                {t(item.label)}
              </span>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
                {...describedBy(item.to)}
              >
                {t(item.label)}
                <UnreadBadge count={badgeCount(item.to)} />
              </NavLink>
            ),
          )}
        </nav>

        <div
          ref={menuRef}
          className="flex items-center gap-3 sm:border-l-2 sm:border-rule sm:pl-6"
        >
          <ConnectionBadge />
          {/* From 1150px: below it the nine links need the width, and the
              avatar's initials already say who is signed in. One line, cut
              short if it must be — a name that wraps makes the header taller,
              and every screen is sized from what the header leaves. */}
          <span className="hidden max-w-[180px] truncate text-[13px] min-[1150px]:block">
            {me?.name || me?.email}
          </span>
          <button
            type="button"
            className="btn btn-ghost relative flex h-[30px] w-[30px] items-center justify-center bg-mute-300 p-0 font-display text-xs font-extrabold text-ink"
            aria-label={t("nav.account")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            {...(menuDescribedBy ? { "aria-describedby": menuDescribedBy } : {})}
          >
            {initials(me?.name, me?.email)}
            {/* On a phone WhatsApp and Correo live inside this menu, so the dot
                is the only place a waiting count can show before it is opened. */}
            {waiting > 0 || mail > 0 ? (
              <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-brand min-[900px]:hidden"
              />
            ) : null}
          </button>

          {menuOpen ? (
            /* Positioned against the header, not against this group.
               `ConnectionBadge` appears and disappears as the socket
               reconnects, which resizes the group on every retry — anchoring
               the panel to it makes the panel twitch under the cursor, and a
               pointer-down that lands a few pixels outside is a pointer-down
               that closes it. The header's box does not move. */
            <div className="absolute top-full right-4 z-20 mt-2 flex w-56 flex-col border-2 border-rule bg-ground shadow-[var(--shadow-lg)] sm:right-8">
              {/* The nav, for the widths that cannot show it inline. */}
              <div className="flex flex-col border-b-2 border-rule min-[900px]:hidden">
                {NAV.map((item) =>
                  item.to === null ? (
                    <span
                      key={item.label}
                      className="px-4 py-2.5 text-sm text-mute-500"
                      title={t("nav.soon")}
                    >
                      {t(item.label)}
                    </span>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className="px-4 py-2.5 text-sm text-ink"
                      {...describedBy(item.to)}
                    >
                      {t(item.label)}
                      <UnreadBadge count={badgeCount(item.to)} />
                    </NavLink>
                  ),
                )}
              </div>

              {/* Recepción and Ajustes survive the redesign but the design does
                  not draw them (.docs/08 §1), so they live here rather than
                  competing with the seven for header width. */}
              <div className="flex flex-col">
                {me?.can_triage ? (
                  <NavLink to="/recepcion" className="px-4 py-2.5 text-sm text-ink">
                    {t("nav.reception")}
                  </NavLink>
                ) : null}
                {can(me, "whatsapp.manage_templates") ? (
                  <NavLink to="/plantillas" className="px-4 py-2.5 text-sm text-ink">
                    {t("nav.templates")}
                  </NavLink>
                ) : null}
                <NavLink to="/ajustes" className="px-4 py-2.5 text-sm text-ink">
                  {t("nav.settings")}
                </NavLink>
                {me?.is_staff ? (
                  /* A plain anchor: the Django admin is server-rendered and
                     outside the router (.docs/04 §10.2). */
                  <a href="/admin/" className="px-4 py-2.5 text-sm text-ink">
                    {t("nav.admin")}
                  </a>
                ) : null}
                {/* One click, no confirmation page: signing out by accident
                    costs a password and a code, which is cheap, and the thing
                    it protects against is someone else's hands on the CRM. */}
                <button
                  type="button"
                  disabled={leaving}
                  onClick={() => void leave()}
                  className="cursor-pointer border-t border-rule px-4 py-2.5 text-left text-sm text-brand-700 disabled:cursor-default disabled:opacity-60"
                >
                  {leaving ? t("nav.signingOut") : t("nav.signOut")}
                </button>
                {signOutFailed ? (
                  <p role="alert" className="mb-0 px-4 pb-2.5 text-xs text-brand-700">
                    {t("nav.signOutFailed")}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </header>

      {/* A mailbox whose token Zoho stopped honouring syncs nothing, and the
          seller has no way to know from the inbox itself. One line, once, with
          the way to fix it. */}
      {mailStopped ? (
        <div className="shrink-0 border-b-2 border-rule bg-panel px-4 py-1.5 text-xs sm:px-8">
          <NavLink to="/ajustes" className="text-brand-700 underline">
            {t("mail.needsReauthBanner")}
          </NavLink>
        </div>
      ) : null}

      <main className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>

      {/* A row in the shell rather than a fixed overlay: a bar pinned over the
          viewport hides whatever is under it, and on a phone that is the send
          button or the last message in a thread. Here it takes its own space
          and nothing has to leave room for it. */}
      <CallBar />
    </div>
  );
}
